import { Box, Grid, Typography } from '@mui/material'
import {
  CarrierType,
  CarrierVisitDirection,
  ContainerDto,
  HoldStatus,
  OrderStatus,
  UnitType,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierTypeIcon } from '@planning/components/CarrierTypeIcon'
import { ContainerVisitStatusChip } from '@planning/components/ContainerVisitStatusChip'
import { useUnitTypeIcon, useUnitTypeLabel } from '@planning/components/UnitType'
import { emptyIndicator } from '@planning/constants'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import {
  BinIcon,
  EditIcon,
  IconButton,
  useContainerAttributesParameters,
  useMinimalsTheme,
} from '@tom-ui/ui'
import _, { Dictionary } from 'lodash'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { ContainerVisit, IContainerVisitOrderItem } from '../stores/ContainerVisitItem'

type Props = {
  containerVisit: ContainerVisit
  container?: ContainerDto
  onClickEdit?: () => void
  onClickDelete?: () => void
  orderCreationOnly?: boolean
}

const templateTranslationMaps: Dictionary<{ key: string; defaultText: string }> = {
  [`${CarrierType.Vessel}_${CarrierType.Train}`]: { key: 'import', defaultText: 'Import' },
  [`${CarrierType.Vessel}_${CarrierType.Truck}`]: { key: 'import', defaultText: 'Import' },
  [`${CarrierType.Vessel}_${CarrierType.Vessel}`]: {
    key: 'transshipment',
    defaultText: 'Transshipment',
  },
  [`${CarrierType.Vessel}_undefined`]: { key: 'storage', defaultText: 'Storage' },

  [`${CarrierType.Truck}_${CarrierType.Train}`]: { key: 'intermodal', defaultText: 'Intermodal' },
  [`${CarrierType.Truck}_${CarrierType.Truck}`]: { key: 'intermodal', defaultText: 'Intermodal' },
  [`${CarrierType.Truck}_${CarrierType.Vessel}`]: { key: 'export', defaultText: 'Export' },
  [`${CarrierType.Truck}_undefined`]: { key: 'storage', defaultText: 'Storage' },

  [`${CarrierType.Train}_${CarrierType.Train}`]: { key: 'intermodal', defaultText: 'Intermodal' },
  [`${CarrierType.Train}_${CarrierType.Truck}`]: { key: 'intermodal', defaultText: 'Intermodal' },
  [`${CarrierType.Train}_${CarrierType.Vessel}`]: { key: 'export', defaultText: 'Export' },
  [`${CarrierType.Train}_undefined`]: { key: 'storage', defaultText: 'Storage' },

  [`undefined_${CarrierType.Train}`]: { key: 'import', defaultText: 'Import' },
  [`undefined_${CarrierType.Truck}`]: { key: 'import', defaultText: 'Import' },
  [`undefined_${CarrierType.Vessel}`]: { key: 'export', defaultText: 'Export' },
}

export const ContainerVisitStatus = {
  Expected: 'Expected',
  OnTerminal: 'OnTerminal',
  Departed: 'Departed',
}

export type ContainerVisitStatus = (typeof ContainerVisitStatus)[keyof typeof ContainerVisitStatus]

export const OrdersListCard: FC<Props> = observer(
  ({ containerVisit, container, onClickEdit, onClickDelete, orderCreationOnly }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { types: containerTypeParameters } = useContainerAttributesParameters()
    const { holdItemStore } = usePlanningStore()

    const getStatus = () => {
      if (!inbound || inbound?.data.status === OrderStatus.Open)
        return ContainerVisitStatus.Expected
      if (!inbound || inbound?.data.status === OrderStatus.Fulfilled)
        return ContainerVisitStatus.OnTerminal
      if (outbound?.data.status === OrderStatus.Open) return ContainerVisitStatus.OnTerminal
      return ContainerVisitStatus.Departed
    }

    const renderContainerAttr = (label: string) => (
      <span
        style={{
          fontSize: '.875rem',
          color: theme.palette.grey[600],
          marginRight: '.5rem',
          fontWeight: 'bold',
        }}
      >
        {label}
      </span>
    )

    const inbound = containerVisit?.[0]
    const outbound = containerVisit?.[1]
    const order = (inbound ?? outbound)?.data

    let icon
    let label

    if (!order) return <></>

    if (container?.unitType === UnitType.Container) {
      const { icon: containerIcon, label: containerLabel } =
        containerTypeParameters[order.containerType]
      icon = containerIcon
      label = containerLabel
    } else {
      icon = useUnitTypeIcon(container?.unitType)
      label = useUnitTypeLabel(container?.unitType)
    }

    const template = (
      inboundCarrierVisitType?: CarrierType,
      outboundCarrierVisitType?: CarrierType,
    ) => {
      return (
        templateTranslationMaps[`${inboundCarrierVisitType}_${outboundCarrierVisitType}`] ?? {
          key: '',
          defaultText: '',
        }
      )
    }

    const InboundAndOutboundInfo: FC<{
      direction: CarrierVisitDirection
      containerData?: IContainerVisitOrderItem
    }> = observer(({ direction, containerData }) => {
      const displyVisitDateTime = () => {
        if (direction === CarrierVisitDirection.Inbound) {
          if (containerData?.visit?.data.ata)
            return `${t('ata', 'ATA')}: ${formatDateTime(containerData?.visit?.data.ata)}`
          else if (containerData?.visit?.data.eta)
            return `${t('eta', 'ETA')}: ${formatDateTime(containerData?.visit?.data.eta)}`
          else if (containerData?.data.carrierVisitType)
            return t('awaitingDropOff', 'Awaiting drop off')
        } else if (direction === CarrierVisitDirection.Outbound) {
          if (containerData?.visit?.data.atd)
            return `${t('atd', 'ATD')}: ${formatDateTime(containerData?.visit?.data.atd)}`
          else if (containerData?.visit?.data.etd)
            return `${t('etd', 'ETD')}: ${formatDateTime(containerData?.visit?.data.etd)}`
          else if (containerData?.data.carrierVisitType)
            return t('awaitingPickUp', 'Awaiting pick up')
        }

        return ''
      }

      return (
        <Box sx={{ display: 'flex', padding: '1rem' }}>
          {CarrierTypeIcon({ type: containerData?.data.carrierVisitType })}
          <Box sx={{ ml: '.75rem' }}>
            <Typography variant='subtitle1'>
              {/* todo: get vessel name if visit type is vessel */}
              {containerData?.visit?.data.identifier ??
                containerData?.data.carrierVisitType ??
                t('unknown', 'Unknown')}
            </Typography>
            <Typography variant='body2' color={theme.palette.grey[600]}>
              {displyVisitDateTime()}
            </Typography>
          </Box>
        </Box>
      )
    })

    const containerJourneyId =
      inbound?.data.holds?.[0]?.containerJourneyId ??
      outbound?.data.holds?.[0]?.containerJourneyId
    const holds =
      containerJourneyId != null
        ? (holdItemStore.holdsByContainerJourneyId[containerJourneyId] ?? [])
        : []
    const numberOfActiveHolds = holds.filter(h => h.status === HoldStatus.Active).length

    const ContainerInformation = () => (
      <Box sx={{ padding: '1rem', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: '.25rem' }}>
            {icon}
            <Typography variant='subtitle1' sx={{ ml: '.5rem' }}>
              {order.containerNumber}
            </Typography>
          </Box>

          {renderContainerAttr(order.containerIsoCode || emptyIndicator)}
          {renderContainerAttr(
            `${order.containerLength ? order.containerLength + 'ft' : emptyIndicator}`,
          )}
          {renderContainerAttr(label)}
          {renderContainerAttr(order.containerHeight || emptyIndicator)}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.customSpacing.m }}>
          <Typography variant='body2'>
            {numberOfActiveHolds} {t('holds', 'Holds')}
          </Typography>
          <ContainerVisitStatusChip label={t(_(getStatus()).lowerFirst())} status={getStatus()} />
          {!orderCreationOnly && onClickEdit && (
            <IconButton variant='contained' onClick={onClickEdit}>
              <EditIcon />
            </IconButton>
          )}

          {!orderCreationOnly &&
            onClickDelete &&
            outbound?.data.status !== OrderStatus.Fulfilled &&
            inbound?.data.status !== OrderStatus.Fulfilled && (
              <IconButton variant='contained' color='error' onClick={onClickDelete}>
                <BinIcon />
              </IconButton>
            )}
        </Box>
      </Box>
    )

    const templateText = () => {
      const { key, defaultText } = template(
        inbound?.data?.carrierVisitType,
        outbound?.data?.carrierVisitType,
      )

      return t(key, defaultText)
    }

    const TransportInformation = () => (
      <Box
        sx={{
          bgcolor: theme.palette.grey[200],
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
        }}
      >
        <Box
          sx={{
            borderRight: `2px solid ${theme.palette.grey[300]}`,
            padding: '1rem',
            width: '220px',
          }}
        >
          <Typography variant='subtitle1'>{templateText()}</Typography>
          <Typography variant='body2' color={theme.palette.grey[600]}>
            {order.referenceNumber ? `Ref: ${order.referenceNumber}` : ''}
          </Typography>
          <Typography variant='body2' color={theme.palette.grey[600]}>
            {order.customerName}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={6}>
            <InboundAndOutboundInfo direction='Inbound' containerData={inbound} />
          </Grid>

          <Grid size={6}>
            <InboundAndOutboundInfo direction='Outbound' containerData={outbound} />
          </Grid>
        </Grid>
      </Box>
    )

    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.customRadius.medium,
          m: '1rem 0',
        }}
      >
        <ContainerInformation />
        <TransportInformation />
      </Box>
    )
  },
)
